import React, { useCallback, useEffect, useState } from 'react';
import { Bell, BellOff, BellRing } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api';

const b64ToUint8 = (b64) => {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

const supported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

// Bell in the /m header: subscribe this device to admin push (new bookings + quote requests).
export default function PushToggle() {
  const [state, setState] = useState('loading'); // loading | unsupported | off | on | denied | busy

  const refresh = useCallback(async () => {
    if (!supported()) { setState('unsupported'); return; }
    if (Notification.permission === 'denied') { setState('denied'); return; }
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    setState(sub ? 'on' : 'off');
  }, []);
  useEffect(() => { refresh().catch(() => setState('unsupported')); }, [refresh]);

  const enable = async () => {
    setState('busy');
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setState(perm === 'denied' ? 'denied' : 'off'); toast.error('Notifications were not allowed.'); return; }
      const { data } = await api.get('/admin/push/vapid-public-key');
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToUint8(data.key) });
      await api.post('/admin/push/subscribe', { ...sub.toJSON(), label: navigator.userAgent.slice(0, 80) });
      setState('on');
      toast.success('This phone will buzz for new bookings and quote requests.');
      api.post('/admin/push/test').catch(() => {});
    } catch (e) {
      setState('off');
      toast.error(e?.response?.data?.detail || 'Could not enable notifications');
    }
  };

  const disable = async () => {
    setState('busy');
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) { await api.delete('/admin/push/subscribe', { data: { endpoint: sub.endpoint } }).catch(() => {}); await sub.unsubscribe(); }
      setState('off');
      toast.success('Notifications off on this phone.');
    } catch { setState('on'); toast.error('Could not disable'); }
  };

  if (state === 'unsupported') {
    return <button onClick={() => toast.info('On iPhone, add the app to your Home Screen first (Share → Add to Home Screen), then open it from there to enable notifications.')}
                   className="p-2 text-white/30" aria-label="Notifications unavailable" data-testid="mobile-push-unsupported"><BellOff className="w-4 h-4" /></button>;
  }
  if (state === 'denied') {
    return <button onClick={() => toast.error('Notifications are blocked for this site — allow them in your phone settings.')}
                   className="p-2 text-white/30" aria-label="Notifications blocked" data-testid="mobile-push-denied"><BellOff className="w-4 h-4" /></button>;
  }
  const on = state === 'on';
  return (
    <button onClick={on ? disable : enable} disabled={state === 'busy' || state === 'loading'}
            className={`p-2 ${on ? 'text-[#E63946]' : 'text-white/50 hover:text-white'} disabled:opacity-50`}
            aria-label={on ? 'Turn off notifications' : 'Turn on notifications'} data-testid="mobile-push-toggle" data-state={state}>
      {on ? <BellRing className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
    </button>
  );
}
